import {
    AssertionDescriptorDisplayValueTypeEnum,
    CaseFileStaticItems,
    CaseStatusTypesEnum,
    CaseSubStatusTypesEnum,
} from '../utilities/Constants';
import {
    ICaseAssertionModel,
    ICaseModel,
    ICaseStatusLookupModel,
    ICaseStatusModel,
} from '../interfaces/ICase';
import { ILookupModel, IMultiSelectOption, IStaticItemLookupModel } from '../interfaces/ILookup';
import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';

import CaseDataCustomLogicHelper from '../utilities/CaseDataCustomLogicHelper';
import CaseFileCheckmateSelectHelper from '../utilities/CaseFileCheckmateSelectHelper';
import Common from '../stores/Common';
import { IAssertionDescriptorModel } from '../interfaces/IAssertionDescriptor';
import { IAssociatedOrganizationModel } from '../interfaces/IOrganization';
import { IDisplaySectionModel } from '../interfaces/IDisplaySectionModel';
import { INoteModel } from '../interfaces/INote';
import { IUserModel } from '../interfaces/IUser';
import { IViewCaseExpertModelWithDefaultEdit } from '../interfaces/ICaseExpert';
import { SameCaseStatusType } from '../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { cloneDeep } from 'lodash';
import { useCheckCaseSameCase } from '../pages/quad/useCheckCaseSameCase';

// Define the types for the context
type CaseFileContextType = {
    assertionDescriptors: IAssertionDescriptorModel[];
    caseDisplayCategories: ILookupModel[];
    caseManagers: IUserModel[];
    caseStatuses: ICaseStatusLookupModel[];
    currentCase: ICaseModel;
    currentCaseExpert: IViewCaseExpertModelWithDefaultEdit | null;
    currentNarrativeCaseAssertion: ICaseAssertionModel | null;
    currentNote: INoteModel | null;
    forceNoteEditorInReadOnlyMode: boolean;
    displaySections: IDisplaySectionModel[];
    isCaseApprovedInSameCaseGroup: boolean | undefined;
    isCasePendingInSameCaseGroup: boolean | undefined;
    isReadOnlyMode: boolean;
    originalCase: ICaseModel;
    showAdditionalFieldsDialog: boolean;
    staticItems: IStaticItemLookupModel[];
    user: IUserModel;
    zoneDefenseCounsels: IAssociatedOrganizationModel[];
    zoneUsers: IUserModel[];

    setAssertionDescriptors: React.Dispatch<React.SetStateAction<IAssertionDescriptorModel[]>>;
    setCaseDisplayCategories: React.Dispatch<React.SetStateAction<ILookupModel[]>>;
    setCaseManagers: React.Dispatch<React.SetStateAction<IUserModel[]>>;
    setCaseStatuses: React.Dispatch<React.SetStateAction<ICaseStatusLookupModel[]>>;
    setCurrentCase: React.Dispatch<React.SetStateAction<ICaseModel>>;
    setCurrentCaseExpert: React.Dispatch<
        React.SetStateAction<IViewCaseExpertModelWithDefaultEdit | null>
    >;
    setCurrentNote: React.Dispatch<React.SetStateAction<INoteModel | null>>;
    setForceNoteEditorInReadOnlyMode: React.Dispatch<React.SetStateAction<boolean>>;
    setCurrentNarrativeCaseAssertion: React.Dispatch<
        React.SetStateAction<ICaseAssertionModel | null>
    >;
    setDisplaySections: React.Dispatch<React.SetStateAction<IDisplaySectionModel[]>>;
    setIsReadOnlyMode: React.Dispatch<React.SetStateAction<boolean>>;
    setOriginalCase: React.Dispatch<React.SetStateAction<ICaseModel>>;
    setShowAdditionalFieldsDialog: React.Dispatch<React.SetStateAction<boolean>>;
    setStaticItems: React.Dispatch<React.SetStateAction<IStaticItemLookupModel[]>>;
    setUser: React.Dispatch<React.SetStateAction<IUserModel>>;
    setZoneDefenseCounsels: React.Dispatch<React.SetStateAction<IAssociatedOrganizationModel[]>>;
    setZoneUsers: React.Dispatch<React.SetStateAction<IUserModel[]>>;

    handleChangeCaseValue: (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => void;
    handleChangeADTextValue: (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => void;
    handleEditNote: (noteGuidToEdit: string) => void;
    handleAssertionDescriptorSearchableSingleSelectChange: (
        selectedOption: IMultiSelectOption,
        assertionDescriptorId: string,
        isBoolean?: boolean
    ) => void;
    handleAssertionDescriptorSearchableMultiSelectChange: (
        selectedOptions: IMultiSelectOption[],
        assertionDescriptorId: string
    ) => void;
    handleStaticItemSearchableSingleSelectChange: (
        selectedOption: IMultiSelectOption,
        staticItemId: number
    ) => void;
    handleNarrativeChange: (assertionDescriptorId: string, value: string) => void;
};

// Create the context
export const CaseFileContext = createContext<CaseFileContextType | undefined>(undefined);

// Create a provider component for the context
type CaseFileProviderProps = {
    children: ReactNode;
};

export function CaseFileProvider({ children }: CaseFileProviderProps) {
    const [originalCase, setOriginalCase] = useState<ICaseModel>({});
    const [currentCase, setCurrentCase] = useState<ICaseModel>({});
    const [user, setUser] = useState<IUserModel>({ active: false } as IUserModel);
    const [caseStatuses, setCaseStatuses] = useState<ICaseStatusLookupModel[]>([]);
    const [caseManagers, setCaseManagers] = useState<IUserModel[]>([]);
    const [displaySections, setDisplaySections] = useState<IDisplaySectionModel[]>([]);
    const [assertionDescriptors, setAssertionDescriptors] = useState<IAssertionDescriptorModel[]>(
        []
    );
    const [caseDisplayCategories, setCaseDisplayCategories] = useState<ILookupModel[]>([]);
    const [staticItems, setStaticItems] = useState<IStaticItemLookupModel[]>([]);
    const [zoneDefenseCounsels, setZoneDefenseCounsels] = useState<IAssociatedOrganizationModel[]>(
        []
    );
    const [zoneUsers, setZoneUsers] = useState<IUserModel[]>([]);
    const [currentNote, setCurrentNote] = useState<INoteModel | null>(null);
    const [forceNoteEditorInReadOnlyMode, setForceNoteEditorInReadOnlyMode] =
        useState<boolean>(false);
    const [currentCaseExpert, setCurrentCaseExpert] =
        useState<IViewCaseExpertModelWithDefaultEdit | null>(null);
    const [showAdditionalFieldsDialog, setShowAdditionalFieldsDialog] = useState<boolean>(false);
    const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean>(true);
    const [currentNarrativeCaseAssertion, setCurrentNarrativeCaseAssertion] =
        useState<ICaseAssertionModel | null>(null);

    const checkSameCaseResult = useCheckCaseSameCase(currentCase.guid);
    const isCaseApprovedInSameCaseGroup =
        checkSameCaseResult.data?.sameCaseStatusTypeId === SameCaseStatusType.Approved;
    const isCasePendingInSameCaseGroup =
        checkSameCaseResult.data?.sameCaseStatusTypeId === SameCaseStatusType.Pending;

    const NUMERIC_TYPES = new Set(['number', 'currency', 'percentage', 'decimal']);

    const setAssertionValue = (
        caData: ICaseAssertionModel,
        type: string,
        value: any,
        descriptor?: IAssertionDescriptorModel
    ): any => {
        // We need descriptor to know if this is Currency / CurrencyWithCents / Decimal
        const displayType = descriptor?.valueDisplayType?.id;
        const isCurrencyWithCents =
            displayType === AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value;
        if (isCurrencyWithCents) {
            // Preserve raw input string for decimal support
            caData.numericValue = value === '' ? '' : value;
        } else if (NUMERIC_TYPES.has(type)) {
            caData.numericValue = parseInt(value, 10);
        } else if (type === 'datetime' || type === 'date') {
            //if user clears it should clear the value and not save assertion.
            caData.dateTimeValue = Common.safeDateFormatOrUndefined(value);
            caData.textValue = value;
        } else {
            caData.textValue = value;
        }
        return caData;
    };

    const handleChangeADTextValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const caseCopy = cloneDeep(currentCase);
        const { name: assertionDescriptorGuid, value, type, inputMode } = event.target;

        const caDataIdx = caseCopy.caseAssertions?.findIndex(
            (ca) => ca.assertionDescriptorGuid === assertionDescriptorGuid
        );

        const found = assertionDescriptors.find((ad) => ad.guid === assertionDescriptorGuid);

        if (caDataIdx !== undefined && caDataIdx !== -1 && caseCopy.caseAssertions) {
            const existingCA = caseCopy.caseAssertions[caDataIdx];

            const updatedCA = {
                ...existingCA,
                userOverride: true,
            };

            setAssertionValue(updatedCA, type, value, found);

            caseCopy.caseAssertions.splice(caDataIdx, 1, updatedCA);
        } else {
            const newCA: ICaseAssertionModel = {
                assertionDescriptorGuid: found?.guid,
                userOverride: true,
            };

            setAssertionValue(newCA, type, value, found);

            if (caseCopy.caseAssertions) {
                caseCopy.caseAssertions.push(newCA);
            } else {
                caseCopy.caseAssertions = [newCA];
            }
        }

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
    };

    const handleNarrativeChange = (assertionDescriptorId: string, value: string) => {
        const caseCopy = cloneDeep(currentCase);

        const caData = caseCopy.caseAssertions?.find(
            (ca) => ca.assertionDescriptorGuid === assertionDescriptorId
        );

        if (caData) {
            caData.textValue = value;
        } else {
            const found = assertionDescriptors.find((ad) => ad.guid === assertionDescriptorId);
            const newCA: ICaseAssertionModel = {
                assertionDescriptorGuid: found?.guid,
                textValue: value,
            };

            if (caseCopy.caseAssertions) caseCopy.caseAssertions.push(newCA);
            else caseCopy.caseAssertions = [newCA];
        }

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
    };

    const handleChangeCaseValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const caseCopy = cloneDeep(currentCase);
        let showFieldsDialog = false;
        const value = event.target.value;

        const { name: fieldName } = event.target;

        switch (fieldName) {
            case 'caseStatus': {
                const lookupMatch = caseStatuses.find((x) => x.id === parseInt(value));

                if (lookupMatch) {
                    currentCase.caseStatus = {
                        ...lookupMatch,
                        displayName: lookupMatch.name,
                    } as ICaseStatusModel;
                    showFieldsDialog =
                        lookupMatch.status &&
                        (lookupMatch.status.id === CaseStatusTypesEnum.Resolved.Value ||
                            (lookupMatch.status.id === CaseStatusTypesEnum.Tendered.Value &&
                                lookupMatch.subStatus?.id ===
                                    CaseSubStatusTypesEnum.Accepted.Value) ||
                            lookupMatch.status.id === CaseStatusTypesEnum.Alt.Value);
                } else {
                    caseCopy.caseStatus = undefined;
                }
                break;
            }
            case 'caseManager': {
                const match = caseManagers.find((x) => x.guid == value);
                if (match) {
                    caseCopy.caseManagerUser = cloneDeep(match);
                } else caseCopy.caseManagerUser = undefined;
                break;
            }
            case 'alternateCaseManager': {
                const match = caseManagers.find((x) => x.guid == value);
                if (match) {
                    currentCase.alternateCaseManagerUser = cloneDeep(match);
                } else currentCase.alternateCaseManagerUser = undefined;
                break;
            }
            case 'caseName':
                caseCopy.caseName = value;
                break;
            case 'caseNumber':
                caseCopy.caseNumber = value;
                break;
            case 'uniqueCaseId':
                caseCopy.uniqueCaseId = value;
                break;
            case 'description':
                caseCopy.description = value;
                break;
            case 'defenseCounsel':
                if (value !== '') {
                    zoneDefenseCounsels.map((defenseCounsel: IAssociatedOrganizationModel) => {
                        if (defenseCounsel.guid == value) {
                            defenseCounsel.priority = 1;
                            if (currentCase.defenseCounsels) {
                                currentCase.defenseCounsels.push(defenseCounsel);
                            } else {
                                currentCase.defenseCounsels = [defenseCounsel];
                            }
                        }
                    });
                }
                break;

            default:
                return;
        }

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
        setShowAdditionalFieldsDialog(showFieldsDialog);
    };

    const handleViewEditNote = useCallback(
        (noteGuidToEdit: string, readOnly?: boolean) => {
            if (!currentCase.notes) return;

            const currentNote = currentCase.notes.find((x) => x.guid == noteGuidToEdit);
            if (currentNote) {
                setCurrentNote(currentNote);
                setForceNoteEditorInReadOnlyMode(readOnly ?? false);
            }
        },
        [currentCase.guid]
    );

    const handleStaticItemSearchableSingleSelectChange = (
        selectedOption: IMultiSelectOption,
        staticItemId: number
    ) => {
        const caseCopy = cloneDeep(currentCase);
        const selectedItem: IMultiSelectOption = { ...selectedOption };
        let showDialog = showAdditionalFieldsDialog;

        switch (staticItemId) {
            case CaseFileStaticItems.CaseStatus.Id: {
                if (selectedItem && selectedItem.id) {
                    const lookupMatch = caseStatuses.find((x) => x.id == selectedItem.id);
                    caseCopy.caseStatus = JSON.parse(JSON.stringify(lookupMatch));
                    if (lookupMatch) {
                        caseCopy.caseStatus = JSON.parse(JSON.stringify(lookupMatch));
                        if (
                            lookupMatch.status &&
                            (lookupMatch.status.id == CaseStatusTypesEnum.Resolved.Value ||
                                (lookupMatch.status.id === CaseStatusTypesEnum.Tendered.Value &&
                                    lookupMatch.subStatus &&
                                    lookupMatch.subStatus.id ===
                                        CaseSubStatusTypesEnum.Accepted.Value) ||
                                lookupMatch.status.id === CaseStatusTypesEnum.Alt.Value)
                        )
                            showDialog = true;
                    }
                } else caseCopy.caseStatus = undefined;
                break;
            }
            case CaseFileStaticItems.CaseManager.Id: {
                if (selectedItem && selectedItem.guid) {
                    const match = zoneUsers.find((x) => x.guid == selectedItem.guid);
                    caseCopy.caseManagerUser = cloneDeep(match);
                } else caseCopy.caseManagerUser = undefined;
                break;
            }
            case CaseFileStaticItems.AlternateCaseManager.Id: {
                if (selectedItem && selectedItem.guid) {
                    const match = zoneUsers.find((x) => x.guid == selectedItem.guid);
                    caseCopy.alternateCaseManagerUser = cloneDeep(match);
                } else caseCopy.alternateCaseManagerUser = undefined;
                break;
            }
            case CaseFileStaticItems.LocalCounsel.Id: {
                // If first ever time adding any Defense Counsel; just add
                if (!caseCopy.defenseCounsels) {
                    if (selectedItem?.guid) {
                        caseCopy.defenseCounsels = [
                            { guid: selectedItem.guid, priority: 1, name: selectedItem.label },
                        ];
                    }
                    break;
                }
                // If no Local Counsel, Add
                const localCounselMatch = caseCopy.defenseCounsels.find((x) => x.priority === 1);
                if (!localCounselMatch) {
                    if (selectedItem.guid) {
                        caseCopy.defenseCounsels.push({
                            guid: selectedItem.guid,
                            priority: 1,
                            name: selectedItem.label,
                        });
                    }
                } else {
                    if (selectedItem?.guid) {
                        localCounselMatch.guid = selectedItem.guid;
                        localCounselMatch.name = selectedItem.label;
                    } else {
                        const matchIndex = caseCopy.defenseCounsels.findIndex(
                            (x) => x.priority === 1
                        );
                        if (matchIndex >= 0) {
                            caseCopy.defenseCounsels.splice(matchIndex, 1);
                        }
                    }
                }
                break;
            }
        }

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
        setShowAdditionalFieldsDialog(showDialog);
    };

    const handleAssertionDescriptorSearchableSingleSelectChange = (
        option: any,
        assertionDescriptorId: string,
        isBoolean?: boolean
    ) => {
        let caseCopy = cloneDeep(currentCase);
        const selectedItem: IMultiSelectOption = option ? { ...option } : undefined;

        caseCopy = CaseFileCheckmateSelectHelper.updateCaseOnSelectedAssertionDescriptorValueChange(
            caseCopy,
            assertionDescriptorId,
            isBoolean || false,
            selectedItem,
            assertionDescriptors
        );

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
    };

    const handleAssertionDescriptorSearchableMultiSelectChange = (
        options: IMultiSelectOption[],
        assertionDescriptorId: string
    ) => {
        const caseCopy = cloneDeep(currentCase);
        const adMatch = assertionDescriptors.find((ad) => ad.guid === assertionDescriptorId);

        if (!caseCopy.caseAssertions) caseCopy.caseAssertions = [];

        caseCopy.caseAssertions = caseCopy.caseAssertions?.filter(
            (ca) => ca.assertionDescriptorGuid !== assertionDescriptorId
        );

        const matchingValues = adMatch?.assertionDescriptorValues?.filter((adv) =>
            options.some((opt) => opt.value === adv.guid)
        );

        if (adMatch) {
            const newCaseAssertions: ICaseAssertionModel[] =
                matchingValues?.map((match) => ({
                    assertionDescriptorGuid: adMatch.guid,
                    assertionDescriptorValueGuid: match.guid,
                })) ?? [];

            caseCopy.caseAssertions = caseCopy.caseAssertions.concat(newCaseAssertions);
        }

        CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);
        setCurrentCase(caseCopy);
    };

    return (
        <CaseFileContext.Provider
            value={{
                assertionDescriptors,
                caseDisplayCategories,
                caseManagers,
                caseStatuses,
                currentCase,
                currentCaseExpert,
                currentNarrativeCaseAssertion,
                currentNote,
                forceNoteEditorInReadOnlyMode,
                displaySections,
                isReadOnlyMode,
                originalCase,
                isCaseApprovedInSameCaseGroup,
                isCasePendingInSameCaseGroup,
                showAdditionalFieldsDialog,
                staticItems,
                user,
                zoneDefenseCounsels,
                zoneUsers,

                setAssertionDescriptors,
                setCaseDisplayCategories,
                setCaseManagers,
                setCaseStatuses,
                setCurrentCase,
                setCurrentCaseExpert,
                setCurrentNarrativeCaseAssertion,
                setCurrentNote,
                setForceNoteEditorInReadOnlyMode,
                setDisplaySections,
                setIsReadOnlyMode,
                setOriginalCase,
                setShowAdditionalFieldsDialog,
                setStaticItems,
                setUser,
                setZoneDefenseCounsels,
                setZoneUsers,

                handleChangeADTextValue,
                handleChangeCaseValue,
                handleEditNote: handleViewEditNote,
                handleNarrativeChange,
                handleAssertionDescriptorSearchableSingleSelectChange,
                handleAssertionDescriptorSearchableMultiSelectChange,
                handleStaticItemSearchableSingleSelectChange,
            }}
        >
            {children}
        </CaseFileContext.Provider>
    );
}
